// RB-MY01-PalmGarden.web/src/components/PlantDetailPage.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi, Plant } from '../services/api';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  CircularProgress,
  Chip,
  Paper
} from '@mui/material';
import { GridContainer, GridItem } from '../components/CustomGrid';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const PlantDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const api = useApi();
  
  const [plant, setPlant] = useState<Plant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchPlant = async () => {
      if (!id) return;
      try {
        setLoading(true);
        const data = await api.getPlantById(parseInt(id));
        if (!data.published) {
          setError('Plant not found');
          return;
        }
        setPlant(data);
      } catch (err) {
        setError('Failed to load plant details');
      } finally {
        setLoading(false);
      }
    };
    fetchPlant();
  }, [id, api]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error || !plant) {
    return (
      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="h5" color="error" gutterBottom>
          {error || 'Plant not found'}
        </Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate('/plants')}
          sx={{ mt: 2 }}
        >
          Back to Plants
        </Button>
      </Box>
    );
  }
  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, mb: 8 }}>
      <Button 
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/plants')}
        sx={{ mb: 3 }}
      >
        Back to Plants
      </Button>
      <Card>
        {plant.imageUrl && (
          <CardMedia
            component="img"
            height="400"
            image={plant.imageUrl}
            alt={plant.name}
            sx={{ objectFit: 'cover' }}
          />
        )}
        <CardContent>
          <Typography variant="h3" component="h1" gutterBottom>
            {plant.name}
          </Typography>
          <Typography variant="h5" color="text.secondary" gutterBottom>
            {plant.type}
          </Typography>
          <Box sx={{ my: 2 }}>
            <Typography variant="body1" paragraph>
              {plant.description}
            </Typography>
          </Box>
          <GridContainer spacing={2} sx={{ mt: 2 }}>
            {plant.nativeHabitat && (
              <GridItem xs={12} sm={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Habitat</Typography>
                  <Typography>{plant.nativeHabitat}</Typography>
                </Paper>
              </GridItem>
            )}
            {plant.preferredClimateZone && (
              <GridItem xs={12} sm={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Climate Zone</Typography>
                  <Typography>{plant.preferredClimateZone}</Typography>
                </Paper>
              </GridItem>
            )}
            {plant.origin && (
              <GridItem xs={12} sm={6}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Origin</Typography>
                  <Typography>{plant.origin}</Typography>
                </Paper>
              </GridItem>
            )}
          </GridContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PlantDetailPage; 